<script>
	import {
		errorMessage,
		masterData,
		loading,
		toastMessage,
	} from "../../store/store";
	import ErrorMessageHandler from "../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import PageHeader from "../../components/PageHeader/PageHeader.svelte";
	import { navigate } from "svelte-routing";
	import { Col, Row } from "@sveltestrap/sveltestrap";
	import CustomButton from "../../components/CustomButton/CustomButton.svelte";
	import CustomSelect from "../../components/CustomSelect/CustomSelect.svelte";
	import CustomInput from "../../components/CustomInput/CustomInput.svelte";
	import { form, field } from "svelte-forms";
	import { required } from "svelte-forms/validators";
	import { extractErrorMessage } from "src/utils/misc";
	import client from "src/services/client";
	import { onMount } from "svelte";
	import ConfirmModal from "../../components/CustomModal/ConfirmModal.svelte";

	export let isEdit;
	export let fetchRuleDetail;
	export let location;

	let isOpen = false;
	let load;
	let prevData;
	const regCheckDuration = /^(?:[0-9]{1,4}|10000)(?:\.[0-9]{1,2})?$/; // checks for numbers between 0 and 10000 with 2 decimal places
	const regCheckMileage = /^(?:[0-9]{1,5}|100000)(?:\.[0-9]{1,2})?$/; // checks for numbers between 0 and 100000 with 2 decimal places

	function checkDuration() {
		return async (value) => {
			return {
				valid: regCheckDuration.test(value),
				name: "exceed_duration",
			};
		};
	}

	function checkMileage() {
		return async (value) => {
			return {
				valid: regCheckMileage.test(value),
				name: "exceed_mileage",
			};
		};
	}

	async function handleCreateRule() {
		let data = {
			carAssetOwnerId: $fleetPartner.value,
			fuelTypeId: $fuelType.value,
			durationInDays: $duration.value,
			mileageInKm: $mileage.value,
		};
		try {
			loading.set(true);
			const res = await client(
				isEdit
					? `/v1/servicing/rules/${isEdit}`
					: "/v1/servicing/rules",
				{
					method: isEdit ? "PUT" : "POST",
					version: "Tasking",
					data,
				}
			);
			if (res.status === 200 || res.status === 204) {
				navigate("/servicing/manage-rules");
				loading.set(false);
				toastMessage.set({
					message: isEdit
						? "The rule has been edited"
						: "The rule has been added",
				});
			}
		} catch (error) {
			errorMessage.set(error.message);
			extractErrorMessage(error);
			loading.set(false);
		}
	}

	async function deleteRule(id) {
		try {
			loading.set(true);
			const res = await client(`/v1/servicing/rules/${id}`, {
				method: "DELETE",
				version: "Tasking",
			});
			if (res.status === 200 || res.status === 204) {
				loading.set(false);
				isOpen = false;
				toastMessage.set({
					message: "The rule has been deleted",
					type: "success",
				});
				navigate("/servicing/manage-rules");
			}
			return;
		} catch (error) {
			extractErrorMessage(error);
		}
	}

	function handleDeleteButton() {
		if (isEdit) {
			isOpen = true;
		} else {
			navigate("/servicing/manage-rules");
		}
	}

	let err;
	let fleetPartnerName = "";
	let fleetPartner = field("fleetPartner", "", [required()]);
	let fuelType = field("fuelType", "", [required()]);
	let duration = field("duration", "", [checkDuration(), required()]);
	let mileage = field("mileage", "", [checkMileage(), required()]);

	let myForm = form(fleetPartner, fuelType, duration, mileage);

	errorMessage.subscribe((value) => {
		err = value;
	});

	loading.subscribe((value) => {
		load = value;
	});

	onMount(async () => {
		if (isEdit) {
			try {
				const ruleData = await fetchRuleDetail(isEdit);
				$fleetPartner.value = ruleData.carAssetOwner.id;
				$fuelType.value = ruleData.fuelType.id;
				$duration.value = ruleData.durationInDays;
				$mileage.value = ruleData.mileageInKm;
				prevData = {
					fleetPartner: ruleData.carAssetOwner.id,
					fuelType: ruleData.fuelType.id,
					duration: ruleData.durationInDays,
					mileage: ruleData.mileageInKm,
				};
				fleetPartnerName = $masterData.carAssetOwners.find(
					(x) => x.id === ruleData.carAssetOwner.id
				).name;
			} catch (error) {
				extractErrorMessage(error);
			}
		} else if (
			location.state.carAssetOwnerId &&
			location.state.fuelTypeId
		) {
			$fleetPartner.value = location.state.carAssetOwnerId;
			$fuelType.value = location.state.fuelTypeId;
		}
	});

	$: editValid =
		JSON.stringify(prevData) ===
		JSON.stringify({
			fleetPartner: $fleetPartner.value,
			fuelType: $fuelType.value,
			duration: $duration.value,
			mileage: $mileage.value,
		});

	$: isValid =
		$fleetPartner.value &&
		$fuelType.value &&
		($duration.value || $duration.value == 0) &&
		($mileage.value || $mileage.value == 0);
</script>

<Row>
	<Col xs={12}>
		<div>
			<ErrorMessageHandler message={err} />
			<Row>
				<Col className="row d-flex align-items-start mx-0">
					<PageHeader
						title={isEdit ? fleetPartnerName : "New Rule"}
						on:click={() => navigate("/servicing/manage-rules")}
					/>
				</Col>
				<Col>
					<div class="d-flex flex-row-reverse">
						<div class="ms-2">
							<CustomButton
								color="primary"
								buttonText="Save"
								on:click={() => handleCreateRule()}
								disabled={load ||
									!isValid ||
									editValid ||
									$myForm.hasError(
										"duration.exceed_duration"
									) ||
									$myForm.hasError("mileage.exceed_mileage")}
								{load}
							/>
						</div>
					</div>
				</Col>
			</Row>
		</div>
	</Col>
	<Col>
		<div class="d-flex justify-content-center">
			<div class="w-50 rule-container mt-3">
				<div>
					<div>
						<span>
							<h5>Rules</h5>
						</span>
					</div>
					<div>
						<span
							>Set rules to determine the vehicle's servicing
							schedule</span
						>
					</div>
				</div>
				<div>
					<div>
						<label for="fleetPartner" class="d-block mt-3">
							<span> Fleet partner </span>
						</label>
					</div>
					<CustomSelect
						options={$masterData.carAssetOwners}
						bind:value={$fleetPartner.value}
						defaultOption="Select fleet partner"
						error={$myForm.hasError("fleetPartner.required")}
						errorText={"Please select a fleet partner"}
					/>
				</div>
				<div>
					<div>
						<label for="fuelType" class="d-block mt-3">
							<span> Select fuel type </span>
						</label>
					</div>
					<CustomSelect
						options={$masterData.fuelTypes}
						defaultOption="Select fuel type"
						bind:value={$fuelType.value}
						error={$myForm.hasError("fuelType.required")}
						errorText={"Fuel type is required"}
					/>
				</div>
				<div>
					<div>
						<label for="duration" class="d-block mt-3">
							<span> Duration </span>
						</label>
					</div>
					<CustomInput
						placeholder="e.g. 10"
						type="number"
						postfix="days"
						bind:value={$duration.value}
						error={$myForm.hasError("duration.required") ||
							$myForm.hasError("duration.exceed_duration")}
						errorText={$myForm.hasError("duration.required")
							? "Duration is required"
							: $myForm.hasError("duration.exceed_duration")
								? "Duration cannot exceed 10000 days"
								: ""}
					/>
				</div>
				<div>
					<div>
						<label for="mileage" class="d-block mt-3">
							<span> Mileage </span>
						</label>
					</div>
					<CustomInput
						placeholder="e.g. 10"
						type="number"
						postfix="km"
						bind:value={$mileage.value}
						error={$myForm.hasError("mileage.required") ||
							$myForm.hasError("mileage.exceed_mileage")}
						errorText={$myForm.hasError("mileage.required")
							? "Mileage is required"
							: $myForm.hasError("mileage.exceed_mileage")
								? "Mileage cannot exceed 100000 km"
								: ""}
					/>
				</div>
				<div class="mt-5">
					<CustomButton
						buttonText={isEdit ? "Delete" : "Cancel"}
						color="danger"
						outline
						on:click={handleDeleteButton}
					/>
				</div>
			</div>
		</div>
	</Col>
</Row>

<ConfirmModal
	open={isOpen}
	toggle={() => {
		isOpen = !isOpen;
	}}
	{err}
	header="Delete rule"
	bodyText="Are you sure you want to delete this rule? This cannot be undone."
	buttonText="Delete"
	handleSubmit={() => deleteRule(isEdit)}
/>

<style>
	.rule-container {
		border: 1px solid var(--fds-outline-variant);
		border-radius: 8px;
		padding: 40px;
	}
</style>
